import { Button, FormControl, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast, useAuth } from "@/comn/hooks";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    APIS,
    URLS,
    SF_DOC_CHNL_MGMT_SRCH,
    SG_DOC_CHNL_MGMT_LIST,
} from "@/tra/tancis/ptli/elctdoc/chnl/services/PtliChnlMgmtService";
import { PTLI0703004Q } from "@/tra/tancis/ptli/elctdoc/box/PTLI0703004Q";

/*
 * Channel Management
 * !== 채널 관리 ==!
 */
export const PTLI0705001Q = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM", "auth_ptli0046", "auth_ptli0047"],
    }); 
    const {t}      = useTranslation();    // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate();       // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast    = useToast();          // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0069" },  // 대상 유형 코드
            { area: "comnCd", comnCd: "COM0080" },  // 채널 신청 진행상태 코드
            { area: "comnCd", comnCd: "COM0064" },  // 활성화 여부 코드
            { area: "comnCd", comnCd: "COM0077" },  // 송수신 유형 코드
        ], 
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        docChnlMgmtSrch: useForm({
            defaultSchema: SF_DOC_CHNL_MGMT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    const grid = {
        docChnlMgmtList: useGrid({
            defaultSchema: SG_DOC_CHNL_MGMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get a list of channel. !== 채널 목록을 조회한다. ==!
        getElctDocChnlList: useFetch({
            api: (page = grid.docChnlMgmtList.page) => APIS.getElctDocChnlList(form.docChnlMgmtSrch.getValues(), page, grid.docChnlMgmtList.size),
            enabled: comnUtils.isEmpty(form.docChnlMgmtSrch.errors),
            key: [grid.docChnlMgmtList.page, grid.docChnlMgmtList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.docChnlMgmtSrch.getValues(),
                    page: grid.docChnlMgmtList.page,
                    size: grid.docChnlMgmtList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true
        })
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getElctDocChnlList: (page: number = 0) => {
            form.docChnlMgmtSrch.handleSubmit(
                () => {
                    grid.docChnlMgmtList.setPage(page);
                    fetch.getElctDocChnlList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Enter Keyword text !== 검색어 엔터 이벤트 ==!
        onEnterKwdTxt: () => {
            grid.docChnlMgmtList.setPage(0);
            fetch.getElctDocChnlList.fetch(0);
        }
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_docChnlMgmtList: {
            cell: {
                intfChnlId: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`/ptli/elctdoc/chnl/ptli0705003s/${rowValues.intfChnlId}`}>{value}</Link>
                },
            },
        },
    };

    const kwdSel = form.docChnlMgmtSrch.watch('kwdSel');

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.docChnlMgmtSrch.schema.trgtTpCd}></Group.Control>
                            <Group.Control {...form.docChnlMgmtSrch.schema.sndrcvDvnCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docChnlMgmtSrch.schema.chnlAplyPrcdStatCd}></Group.Control>
                            <Group.Control {...form.docChnlMgmtSrch.schema.actvtYn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Label label={"L_KWD"} />
                            <Group.Col padding={1}>
                                <Layout direction="row" gap={1}>
                                    <FormControl 
                                        {...form.docChnlMgmtSrch.schema.kwdSel}
                                    />
                                    <FormControl 
                                        {...form.docChnlMgmtSrch.schema.kwdTxt} 
                                        readOnly={!kwdSel}
                                        onEnter={handler.onEnterKwdTxt}
                                    />
                                </Layout>
                            </Group.Col>
                        </Group.Row>
                    </Group.Section>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.docChnlMgmtSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getElctDocChnlList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.docChnlMgmtList.grid}
                            data={fetch.getElctDocChnlList.data?.elctDocChnlList}
                            render={render.grid_docChnlMgmtList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="new"
                        onClick={() => {
                            navigate(URLS.ptli0705002s);
                        }}
                    />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
